<template>
    <div class="modal-card card">
        <form @submit.prevent="onPrimaryClick">
            <div class="card-header">
                <!-- Title -->
                <h4 class="card-header-title">
                    Create certificate
                </h4>
                <!-- Close -->
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"
                    @click="$store.dispatch('hideModal')"></button>
            </div>

            <!-- Steps -->
            <div class="cert-steps">
                <div class="steps-track">
                    <template v-for="(step, index) in steps" :key="step.n">
                        <div v-if="index" class="step-line" :class="{ active: current > step.n - 1 }"></div>
                        <div class="step-item">
                            <button type="button" class="step-circle" :class="{
                                active: current === step.n,
                                completed: current > step.n && isStepValid(step.n),
                                invalid: visited.includes(step.n) && current !== step.n && !isStepValid(step.n)
                            }" :aria-current="current === step.n ? 'step' : null" @click="goTo(step.n)">
                                <i v-if="current > step.n && isStepValid(step.n)" class="fe fe-check"></i>
                                <i v-else-if="current === step.n" class="fe" :class="step.icon"></i>
                                <span v-else>{{ step.n }}</span>
                            </button>
                            <small class="step-label" :class="{ active: current === step.n }">{{ step.label }}</small>
                        </div>
                    </template>
                </div>
            </div>

            <div class="card-body">
                <div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

                <!-- 1. Domains -->
                <div v-show="current === 1">
                    <p class="step-say">Name the host your tag is served from. Add any other hosts as subject
                        alternative names — one certificate covers all of them.</p>
                    <input-field label="Common name" type="text" v-model="certificate.commonName"
                        placeholder="tag.example.com" :validate="hostValidation('Common name')" :helpText="'Fully qualified hostname. This cannot be changed after the certificate is issued.'"/>
                    <input-field label="Subject alternative names" type="multiselect" v-model="certificate.sans"
                        placeholder="Type a hostname and press enter" :validate="getValidation('Sans')" />
                </div>

                <!-- 2. Contact -->
                <div v-show="current === 2">
                    <p class="step-say">Your certificate authority sends issuance, renewal and expiry notices to this
                        person. A shared mailbox outlasts an individual one.</p>
                    <div class="row g-3">
                        <div class="col-md-6">
                            <input-field label="First name" type="text" v-model="admin.firstName"
                                :validate="getValidation('First name')" />
                        </div>
                        <div class="col-md-6">
                            <input-field label="Last name" type="text" v-model="admin.lastName"
                                :validate="getValidation('Last name')" />
                        </div>
                        <div class="col-md-7">
                            <input-field label="Email" type="text" v-model="admin.email" placeholder="aut@aqfer.com"
                                :validate="emailValidation" />
                        </div>
                        <div class="col-md-5">
                            <input-field label="Phone" type="text" v-model="admin.phone"
                                :validate="getValidation('Phone')" />
                        </div>
                    </div>
                </div>

                <!-- 3. Organization -->
                <div v-show="current === 3">
                    <p class="step-say">The legal entity requesting the certificate. Your certificate authority
                        verifies these details against public records.</p>
                    <div class="row g-3">
                        <div class="col-md-7">
                            <input-field label="Organization name" type="text" v-model="organization.organizationName"
                                :validate="getValidation('Organization name')" />
                        </div>
                        <div class="col-md-5">
                            <input-field label="Unit" type="text" v-model="organization.organizationUnit"
                                placeholder="Optional" />
                        </div>
                        <div class="col-md-4">
                            <input-field label="Country" type="text" v-model="organization.country" placeholder="US"
                                :validate="getValidation('Country')"
                                :toolTip="'Two-letter ISO country code, for example US'" />
                        </div>
                        <div class="col-md-4">
                            <input-field label="State" type="text" v-model="organization.state"
                                :validate="getValidation('State')" />
                        </div>
                        <div class="col-md-4">
                            <input-field label="Locality" type="text" v-model="organization.locality"
                                :validate="getValidation('Locality')" />
                        </div>
                        <div class="col-md-8">
                            <input-field label="Address line 1" type="text" v-model="organization.addressLineOne"
                                :validate="getValidation('Address line 1')" />
                        </div>
                        <div class="col-md-4">
                            <input-field label="Postal code" type="text" v-model="organization.postalCode"
                                :validate="getValidation('Postal code')" />
                        </div>
                        <div class="col-md-8">
                            <input-field label="Address line 2" type="text" v-model="organization.addressLineTwo"
                                placeholder="Optional" />
                        </div>
                        <div class="col-md-4">
                            <input-field label="Phone" type="text" v-model="organization.phone"
                                :validate="getValidation('Organization phone')" />
                        </div>
                    </div>

                    <div class="subject-panel">
                        <div class="d-flex align-items-center gap-3 flex-wrap">
                            <div>
                                <div class="subject-title">Certificate information</div>
                                <div class="field-hint mb-0">The four fields embedded in the certificate itself. The
                                    address, postal code, unit and phone above are not part of it.</div>
                            </div>
                            <div class="form-check ms-auto mb-0">
                                <input class="form-check-input" type="checkbox" id="sameAsOrg" v-model="subjectSameAsOrg">
                                <label class="form-check-label" for="sameAsOrg">Same as organization</label>
                            </div>
                        </div>
                        <dl class="pairs subject-readout" v-if="subjectSameAsOrg">
                            <div v-for="field in subjectFields" :key="field.label">
                                <dt>{{ field.label }}</dt>
                                <dd :class="{ 'is-blank': !field.value }">{{ field.value || 'Not set' }}</dd>
                            </div>
                        </dl>
                        <div class="row g-3 mt-1" v-else>
                            <div class="col-md-6">
                                <input-field label="Organization name" type="text"
                                    v-model="certificateInfo.organizationName"
                                    :validate="getValidation('Subject organization name')" />
                            </div>
                            <div class="col-md-6">
                                <input-field label="Country" type="text" v-model="certificateInfo.country"
                                    :validate="getValidation('Subject country')" />
                            </div>
                            <div class="col-md-6">
                                <input-field label="State" type="text" v-model="certificateInfo.state"
                                    :validate="getValidation('Subject state')" />
                            </div>
                            <div class="col-md-6">
                                <input-field label="Locality" type="text" v-model="certificateInfo.locality"
                                    :validate="getValidation('Subject locality')" />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 4. Review -->
                <div v-show="current === 4">
                    <p class="step-say">Creating the certificate opens a signing request with your certificate
                        authority. Check the details — a mistake here means starting over.</p>
                    <div class="row g-3">
                        <div class="col-md-6">
                            <section class="review-box">
                                <h6 class="review-head">Domains
                                    <button type="button" class="btn-link-sm" @click="goTo(1)">Edit</button>
                                </h6>
                                <dl class="pairs pairs-wide">
                                    <div>
                                        <dt>Common name</dt>
                                        <dd :class="{ 'is-blank': !certificate.commonName }">
                                            {{ certificate.commonName || 'Not set' }}</dd>
                                    </div>
                                    <div>
                                        <dt>SANs</dt>
                                        <dd>
                                            <div class="chips" v-if="sans.length">
                                                <span class="chip" v-for="(san, index) in sans"
                                                    :key="`review-san-${index}`">{{ san }}</span>
                                            </div>
                                            <span v-else class="is-blank">None</span>
                                        </dd>
                                    </div>
                                </dl>
                            </section>
                        </div>
                        <div class="col-md-6">
                            <section class="review-box">
                                <h6 class="review-head">Admin contact
                                    <button type="button" class="btn-link-sm" @click="goTo(2)">Edit</button>
                                </h6>
                                <dl class="pairs">
                                    <div>
                                        <dt>Name</dt>
                                        <dd>{{ [admin.firstName, admin.lastName].filter(Boolean).join(' ') }}</dd>
                                    </div>
                                    <div>
                                        <dt>Email</dt>
                                        <dd>{{ admin.email }}</dd>
                                    </div>
                                    <div>
                                        <dt>Phone</dt>
                                        <dd>{{ admin.phone }}</dd>
                                    </div>
                                </dl>
                            </section>
                        </div>
                        <div class="col-md-6">
                            <section class="review-box">
                                <h6 class="review-head">Organization
                                    <button type="button" class="btn-link-sm" @click="goTo(3)">Edit</button>
                                </h6>
                                <dl class="pairs">
                                    <div>
                                        <dt>Name</dt>
                                        <dd>{{ organization.organizationName }}</dd>
                                    </div>
                                    <div v-if="organization.organizationUnit">
                                        <dt>Unit</dt>
                                        <dd>{{ organization.organizationUnit }}</dd>
                                    </div>
                                    <div>
                                        <dt>Address</dt>
                                        <dd>{{ organization.addressLineOne }}</dd>
                                    </div>
                                    <div v-if="organization.addressLineTwo">
                                        <dt>Address 2</dt>
                                        <dd>{{ organization.addressLineTwo }}</dd>
                                    </div>
                                    <div>
                                        <dt>Locality</dt>
                                        <dd>{{ organization.locality }}</dd>
                                    </div>
                                    <div>
                                        <dt>State</dt>
                                        <dd>{{ organization.state }}</dd>
                                    </div>
                                    <div>
                                        <dt>Postal code</dt>
                                        <dd>{{ organization.postalCode }}</dd>
                                    </div>
                                    <div>
                                        <dt>Country</dt>
                                        <dd>{{ organization.country }}</dd>
                                    </div>
                                    <div>
                                        <dt>Phone</dt>
                                        <dd>{{ organization.phone }}</dd>
                                    </div>
                                </dl>
                            </section>
                        </div>
                        <div class="col-md-6">
                            <section class="review-box">
                                <h6 class="review-head">Certificate information
                                    <span class="badge-soft" :class="subjectSameAsOrg ? 'bs-ok' : 'bs-info'">
                                        {{ subjectSameAsOrg ? 'Same as organization' : 'Entered separately' }}
                                    </span>
                                    <button type="button" class="btn-link-sm" @click="goTo(3)">Edit</button>
                                </h6>
                                <dl class="pairs">
                                    <div v-for="field in subjectFields" :key="`review-${field.label}`">
                                        <dt>{{ field.label }}</dt>
                                        <dd :class="{ 'is-blank': !field.value }">{{ field.value || 'Not set' }}</dd>
                                    </div>
                                </dl>
                            </section>
                        </div>
                    </div>
                    <div class="next-panel">
                        <div class="subject-title">What happens next</div>
                        <p class="field-hint mb-0">aqfer generates a CSR for this request. Have your certificate
                            authority sign it, then upload the signed certificate from the certificate page to start
                            deployment.</p>
                    </div>
                </div>
            </div>

            <div class="card-footer d-flex align-items-center gap-3">
                <span class="field-hint mb-0">
                    <template v-if="current === 4">Review before creating</template>
                    <template v-else>Step {{ current }} of 4</template>
                </span>
                <div class="ms-auto d-flex gap-2">
                    <button type="button" class="btn btn-secondary" v-if="current === 1"
                        @click="$store.dispatch('hideModal')">Cancel</button>
                    <button type="button" class="btn btn-secondary" v-else @click="goTo(current - 1)">Back</button>
                    <button type="submit" class="btn btn-primary" :disabled="creating || !canAdvance"
                        :title="blockers.length ? blockers.join(', ') : null">
                        <div v-if="creating" class="spinner-border spinner-border-sm" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                        <div v-else>{{ current === 4 ? 'Create certificate' : 'Continue' }}</div>
                    </button>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import { mapActions } from "vuex/dist/vuex.cjs.js";
import InputField from "../../components/InputField.vue";

const HOSTNAME = /^(\*\.)?([a-z0-9](-?[a-z0-9])*\.)+[a-z]{2,}$/i;

export default {
    components: { InputField },
    name: "AddCertificate",
    data() {
        return {
            creating: false,
            error: "",
            current: 1,
            visited: [1],
            subjectSameAsOrg: true,
            certificate: {},
            admin: {},
            certificateInfo: {},
            organization: {},
            steps: [
                { n: 1, label: "Domains", icon: "fe-globe" },
                { n: 2, label: "Contact", icon: "fe-user" },
                { n: 3, label: "Organization", icon: "fe-briefcase" },
                { n: 4, label: "Review", icon: "fe-clipboard" },
            ],
        }
    },
    computed: {
        sans() {
            return this.certificate.sans || [];
        },
        // The subject actually sent: the organization details unless they were overridden.
        effectiveSubject() {
            if (!this.subjectSameAsOrg) return this.certificateInfo;
            return {
                organizationName: this.organization.organizationName || "",
                country: this.organization.country || "",
                state: this.organization.state || "",
                locality: this.organization.locality || "",
            };
        },
        // The subject as it will be sent, labelled the way the certificate page labels it.
        subjectFields() {
            const subject = this.effectiveSubject;
            return [
                { label: "Name", value: subject.organizationName },
                { label: "Country", value: subject.country },
                { label: "State", value: subject.state },
                { label: "Locality", value: subject.locality },
            ];
        },
        blockers() {
            return this.stepBlockers(this.current);
        },
        canAdvance() {
            if (this.current === 4) return [1, 2, 3].every(n => this.isStepValid(n));
            return this.blockers.length === 0;
        },
        emailValidation() {
            return [
                { type: 'required', errMsg: 'Email is required' },
                { type: 'email', errMsg: 'Enter a complete email address, for example aut@aqfer.com' },
            ];
        },
    },
    methods: {
        ...mapActions({
            createCertificate: "aut/createCertificate",
        }),
        getValidation(name) {
            return [
                { type: 'required', errMsg: `${name} is required` }
            ]
        },
        hostValidation(name) {
            return [
                { type: 'required', errMsg: `${name} is required` },
                { type: 'regex', pattern: HOSTNAME, errMsg: `Enter a fully qualified hostname, for example tag.example.com` },
            ]
        },
        stepBlockers(step) {
            const missing = [];
            const require = (label, value) => { if (!value || !String(value).trim()) missing.push(label); };

            if (step === 1) {
                require("Common name", this.certificate.commonName);
                if (this.certificate.commonName && !HOSTNAME.test(String(this.certificate.commonName).trim())) {
                    missing.push("Common name must be a fully qualified hostname");
                }
                if (!this.sans.length) missing.push("Subject alternative names");
            }
            if (step === 2) {
                require("First name", this.admin.firstName);
                require("Last name", this.admin.lastName);
                require("Phone", this.admin.phone);
                if (!this.admin.email || !/^\S+@\S+\.\S+$/.test(this.admin.email)) missing.push("Email");
            }
            if (step === 3) {
                require("Organization name", this.organization.organizationName);
                require("Country", this.organization.country);
                require("State", this.organization.state);
                require("Locality", this.organization.locality);
                require("Address line 1", this.organization.addressLineOne);
                require("Postal code", this.organization.postalCode);
                require("Organization phone", this.organization.phone);
                if (!this.subjectSameAsOrg) {
                    require("Subject organization name", this.certificateInfo.organizationName);
                    require("Subject country", this.certificateInfo.country);
                    require("Subject state", this.certificateInfo.state);
                    require("Subject locality", this.certificateInfo.locality);
                }
            }
            return missing;
        },
        isStepValid(step) {
            return this.stepBlockers(step).length === 0;
        },
        goTo(step) {
            if (step < 1 || step > 4) return;
            // Moving forward requires every step in between to be complete.
            if (step > this.current) {
                for (let n = this.current; n < step; n++) {
                    if (!this.isStepValid(n)) { this.current = n; return; }
                }
            }
            this.current = step;
            if (!this.visited.includes(step)) this.visited.push(step);
        },
        onPrimaryClick() {
            if (this.current < 4) {
                this.goTo(this.current + 1);
                return;
            }
            this.onAddCertificate();
        },
        async onAddCertificate() {
            this.creating = true;
            this.error = "";
            let isCertCreated = false;
            let toastOptions;
            this.certificate.sans = this.sans.map(san => String(san).trim());
            this.certificate.admin = this.admin;
            this.certificate.certificateInfo = { ...this.effectiveSubject };
            this.certificate.org = this.organization;
            await this.createCertificate(this.certificate).then(() => {
                toastOptions = {
                    message: "Certificate created successfully.",
                    type: "success"
                }
                this.$store.dispatch('hideModal')
                isCertCreated = true;
            }).catch(err => {
                console.log("error: ", err)
                let error = null
                if (err.response)
                    error = err.response.data.error;
                else error = err.message;
                this.error = error || "Something went wrong while creating certificate. Please contact support.";
                toastOptions = {
                    message: this.error,
                    type: "error"
                }
            })
            this.creating = false;
            this.$store.dispatch("addToast", toastOptions);
            if (isCertCreated) {
                this.$store.state.aut.listCertTrigger = !this.$store.state.aut.listCertTrigger // trigger to get the new list of certificates
            }
        }
    }
};
</script>

<style lang="scss" scoped>
@use '../../theme/scss/user-variables' as *;

// ── Step wizard — same treatment as the onboarding wizard ───────────────────
.cert-steps {
    padding: 1.2rem 1.4rem 1rem;
}

.steps-track {
    display: flex;
    align-items: flex-start;
    justify-content: center;
}

.step-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: .4rem;
    flex: 0 0 auto;
    max-width: 6rem;
}

.step-circle {
    width: 36px;
    height: 36px;
    padding: 0;
    border-radius: 50%;
    background-color: #e9ecef;
    border: 2px solid #dee2e6;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6c757d;
    font-weight: 700;
    font-size: .82rem;
    transition: background-color .25s, border-color .25s, color .25s, box-shadow .25s;

    &.active {
        background-color: #2c7be5;
        border-color: #2c7be5;
        color: #fff;
        box-shadow: 0 0 0 4px rgba(44, 123, 229, .18);
    }

    &.completed {
        background-color: #00a854;
        border-color: #00a854;
        color: #fff;
    }

    &.invalid {
        background-color: transparent;
        border-color: #E63757;
        color: #E63757;
    }

    body.dark-mode & {
        background-color: $dark-input-bg;
        border-color: $dark-border-color;
        color: $dark-text-secondary;

        &.active {
            background-color: #2c7be5;
            border-color: #2c7be5;
            color: #fff;
        }

        &.completed {
            background-color: #00a854;
            border-color: #00a854;
            color: #fff;
        }

        &.invalid {
            background-color: transparent;
            border-color: #E63757;
            color: #f28a9c;
        }
    }
}

.step-line {
    flex: 1 1 12px;
    max-width: 48px;
    height: 2px;
    margin: 17px 6px 0;
    background-color: #dee2e6;
    transition: background-color .3s;

    &.active {
        background-color: #00a854;
    }

    body.dark-mode & {
        background-color: $dark-border-color;

        &.active {
            background-color: #00a854;
        }
    }
}

.step-label {
    font-size: .75rem;
    color: #6E84A3;
    text-align: center;
    line-height: 1.3;
    overflow-wrap: anywhere;

    &.active {
        color: #12263F;
        font-weight: 600;
    }

    body.dark-mode & {
        color: $dark-text-secondary;

        &.active {
            color: $dark-text-primary;
        }
    }
}

// ── Step content ────────────────────────────────────────────────────────────
.step-say {
    font-size: .8125rem;
    color: #6E84A3;
    margin: -.25rem 0 1.15rem;

    body.dark-mode & {
        color: $dark-text-secondary;
    }
}

.field-hint {
    font-size: .75rem;
    color: #95AAC9;
    margin-top: .35rem;

    body.dark-mode & {
        color: $dark-text-secondary;
    }
}

.subject-panel {
    margin-top: 1.5rem;
    background: #F9FBFD;
    border: 1px solid #E3EBF6;
    border-radius: .5rem;
    padding: 1rem 1.15rem;

    body.dark-mode & {
        background: $dark-input-bg;
        border-color: $dark-border-color;
    }
}

.subject-title {
    font-weight: 600;
    font-size: .875rem;
}

.subject-readout {
    margin-top: .9rem;
    border-top: 1px solid #E3EBF6;

    body.dark-mode & {
        border-top-color: $dark-border-color;
    }
}

.next-panel {
    margin-top: 1.5rem;
    background: #F0F6FE;
    border: 1px solid #BBD4F7;
    border-radius: .5rem;
    padding: .9rem 1.15rem;

    body.dark-mode & {
        background: rgba(44, 123, 229, .12);
        border-color: rgba(44, 123, 229, .35);
    }
}

.review-box {
    height: 100%;
    border: 1px solid #E3EBF6;
    border-radius: .5rem;
    background: #F9FBFD;
    padding: 1rem 1.15rem;

    body.dark-mode & {
        background: $dark-input-bg;
        border-color: $dark-border-color;
    }
}

.review-head {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: .5rem;
    font-size: .9375rem;
    color: #12263F;
    font-weight: 600;
    margin-bottom: .75rem;
    padding-bottom: .6rem;
    border-bottom: 1px solid #E3EBF6;

    body.dark-mode & {
        color: $dark-text-primary;
        border-bottom-color: $dark-border-color;
    }
}

/* The Edit link sits at the end of the heading row, never squeezing the title. */
.review-head .btn-link-sm {
    margin-left: auto;
}

/* Badges — scoped styles do not reach across components, so the soft badge
   used on the certificate page is redefined here. */
.badge-soft {
    font-size: .6875rem;
    font-weight: 600;
    border-radius: .25rem;
    padding: .2rem .45rem;
    white-space: nowrap;
}

.bs-ok {
    background: #E4FAF1;
    color: #0B7A4B;

    body.dark-mode & {
        background: rgba(0, 217, 126, .16);
        color: #4ade8a;
    }
}

.bs-info {
    background: #EAF3FD;
    color: #1A5FB8;

    body.dark-mode & {
        background: rgba(44, 123, 229, .18);
        color: #7eb5ff;
    }
}

.btn-link-sm {
    border: none;
    background: none;
    color: #2C7BE5;
    font-size: .75rem;
    padding: 0 0 0 .35rem;

    body.dark-mode & {
        color: #7eb5ff;
    }
}

.chips {
    display: flex;
    flex-wrap: wrap;
    gap: .4rem;
}

.review-box .chip {
    background: #fff;

    body.dark-mode & {
        background: $dark-card-bg;
    }
}

.chip {
    border: 1px solid #E3EBF6;
    background: #F9FBFD;
    border-radius: .3rem;
    padding: .25rem .55rem;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: .8125rem;
    color: #3B506C;
    word-break: break-all;

    body.dark-mode & {
        background: $dark-input-bg;
        border-color: $dark-border-color;
        color: $dark-text-primary;
    }
}

.pairs {
    margin: 0;
}

.pairs>div {
    display: grid;
    grid-template-columns: 5.5rem 1fr;
    gap: 1rem;
    padding: .5rem 0;
    align-items: baseline;
}

.pairs-wide>div {
    grid-template-columns: 7.5rem 1fr;
}

.pairs>div+div {
    border-top: 1px solid #EDF2F9;

    body.dark-mode & {
        border-top-color: $dark-border-color;
    }
}

.pairs dt {
    font-weight: 400;
    color: #6E84A3;
    font-size: .8125rem;
    margin: 0;

    body.dark-mode & {
        color: $dark-text-secondary;
    }
}

.pairs dd {
    margin: 0;
    word-break: break-word;
}

.is-blank {
    color: #B1C2D9;

    body.dark-mode & {
        color: $dark-text-muted;
    }
}
</style>
